import { Box, CSSObject } from '@mui/material';

import { AppIcon, AppIconProps } from './AppIcon';
import type { ICON_SIZES } from './AppIcon';
import { COLOR_TOKENS, ColorToken } from '../colors/colors';
import { SHADOWS } from '../shadows/shadows';
import { RADIUS } from '../spacing/spacing';
export const FEATURED_ICON_SIZES = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'xl',
} as const;

type FeaturedIconSize = keyof typeof FEATURED_ICON_SIZES;

const APP_ICON_SIZES: Record<FeaturedIconSize, keyof typeof ICON_SIZES> = {
  sm: '16px',
  md: '20px',
  lg: '24px',
  xl: '28px',
};

type FeaturedIconVariants = 'square' | 'round';

const COMMON_STYLES: CSSObject = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
  background: COLOR_TOKENS['Colors/Background/bg-primary'],
};

const BORDER_STYLES: CSSObject = {
  border: `1px solid ${COLOR_TOKENS['Gray/200']}`,
  boxShadow: `${SHADOWS['shadow-skeumorphic-inner-border']}, ${SHADOWS['shadow-skeumorphic-inner']}, ${SHADOWS['shadow-xs']}`,
};

const CONTAINER_SX: Record<FeaturedIconSize, CSSObject> = {
  sm: {
    width: '32px',
    height: '32px',
    padding: '8px',
    borderRadius: RADIUS['radius-sm'],
    ...COMMON_STYLES,
  },
  md: {
    width: '40px',
    height: '40px',
    padding: '10px',
    borderRadius: RADIUS['radius-md'],
    ...COMMON_STYLES,
  },
  lg: {
    width: '48px',
    height: '48px',
    padding: '12px',
    borderRadius: RADIUS['radius-lg'],
    ...COMMON_STYLES,
  },
  xl: {
    width: '56px',
    height: '56px',
    padding: '14px',
    borderRadius: RADIUS['radius-xl'],
    ...COMMON_STYLES,
  },
};

export type FeaturedIconProps = Readonly<{
  iconName: AppIconProps['iconName'];
  size: FeaturedIconSize;
  shape?: FeaturedIconVariants;
  color: AppIconProps['color'];
  fill?: AppIconProps['fill'];
  strokeWidth?: AppIconProps['strokeWidth'];
  backgroundColor?: ColorToken;
  border?: boolean;
  borderColor?: ColorToken;
}>;

export const FeaturedIcon = ({
  iconName,
  size,
  shape = 'square',
  color,
  fill,
  strokeWidth,
  backgroundColor,
  border = true,
  borderColor,
}: FeaturedIconProps) => {
  const SX = {
    ...CONTAINER_SX[size],
    ...(border ? BORDER_STYLES : {}),
    borderRadius: shape === 'round' ? RADIUS['radius-full'] : CONTAINER_SX[size].borderRadius,
    backgroundColor: backgroundColor ? COLOR_TOKENS[backgroundColor] : undefined,
    borderColor: borderColor ? COLOR_TOKENS[borderColor] : undefined,
  };

  return (
    <Box sx={SX}>
      <AppIcon
        color={color}
        fill={fill}
        strokeWidth={strokeWidth}
        iconName={iconName}
        diameter={APP_ICON_SIZES[size]}
      />
    </Box>
  );
};
